import { useIntl } from "react-intl";
import * as Yup from "yup";

// ── shared attachment schema ─────────────────────────────────────────────────
const attachmentSchema = Yup.object().shape({
  _destroy: Yup.boolean(),

  path: Yup.mixed().when("_destroy", {
    is: true,
    then: Yup.mixed().nullable(), // destroyed → skip
    otherwise: Yup.mixed().required()
  }),

  position: Yup.number().when("_destroy", {
    is: true,
    then: Yup.number().nullable(), // destroyed → skip
    otherwise: Yup.number().required()
  })
});

// ── option schema (used only when the flashcard has options) ─────────────────
const getOptionSchema = ({ answerIsRequired, imageRequired }) =>
  Yup.object().shape({
    currentTab: Yup.string(),

    content: Yup.string().when("currentTab", {
      is: (tab) => tab === "text" || tab === "text_image",
      then: Yup.string().required(answerIsRequired).nullable(),
      otherwise: Yup.string().nullable()
    }),

    correct: Yup.boolean(),

    // option image — array with max 1 active item
    flashcard_attachments_attributes: Yup.array()
      .of(attachmentSchema)
      .when("currentTab", {
        is: (tab) => tab === "image" || tab === "text_image",
        then: Yup.array().test(
          "atLeastOneActiveImage",
          imageRequired,
          (attachments) => {
            if (!Array.isArray(attachments)) return false;
            return attachments.filter((a) => !a._destroy).length >= 1;
          }
        ),
        otherwise: Yup.array().nullable()
      })
  });

// Sibling fields must not reference each other via when(): Yup builds a
// dependency graph from when() references and a two-way reference is a
// "Cyclic dependency" error. The content↔attachments rules therefore gate
// on currentTab with when() and read the sibling value inside .test()
// through this.parent, which stays out of that graph.
const getMemoryOptionSchema = ({ answerIsRequired, imageRequired }) =>
  Yup.object().shape({
    currentTab: Yup.string(),

    content: Yup.string().when("currentTab", {
      is: (tab) => tab === "text" || tab === "text_image",
      then: Yup.string()
        .nullable()
        .test("memoryContentRequired", answerIsRequired, function (value) {
          const attachments = this.parent.flashcard_attachments_attributes;
          if (!(attachments || []).some((a) => !a._destroy)) return true;
          return `${value ?? ""}`.trim() !== "";
        }),
      otherwise: Yup.string().nullable()
    }),

    correct: Yup.boolean(),

    flashcard_attachments_attributes: Yup.array()
      .of(attachmentSchema)
      .when("currentTab", {
        is: (tab) => tab === "image" || tab === "text_image",
        then: Yup.array().test(
          "atLeastOneActiveImage",
          imageRequired,
          function (attachments) {
            const content = this.parent.content;
            if (`${content ?? ""}`.trim() === "") return true;
            if (!Array.isArray(attachments)) return false;
            return attachments.filter((a) => !a._destroy).length >= 1;
          }
        ),
        otherwise: Yup.array().nullable()
      })
  });

// ── main schema ───────────────────────────────────────────────────────────────
export const ADD_EDIT_FLASHCARD_SCHEMA = () => {
  const { messages } = useIntl();
  const {
    flashcard: {
      validation: {
        questionBodyRequired,
        difficultyLevelRequired,
        topicRequired,
        subTopicRequired,
        answerIsRequired,
        imageRequired,
        correctAnswerRequired,
        explanationBodyRequired,
        maxOneAnswerMemoryOnly
      }
    }
  } = messages;

  return Yup.object().shape({
    flashcards: Yup.array().of(
      Yup.object().shape({
        question_body: Yup.string().required(questionBodyRequired),
        difficulty_level: Yup.mixed().required(
          difficultyLevelRequired
        ),
        mode_eligibility: Yup.string().required(
          "mode eligibility is required."
        ),

        flashcard_topic_id: Yup.mixed()
          .nullable()
          .required(topicRequired),

        flashcard_sub_topic_id: Yup.mixed()
          .nullable()
          .required(subTopicRequired),

        // question images — optional, max 3
        flashcard_attachments_attributes: Yup.array()
          .of(attachmentSchema)
          .nullable(),

        // memory_only flashcards have 0 or 1 option (the correct answer);
        // all other modes require at least one correct option
        flashcard_options_attributes: Yup.array().when(
          "mode_eligibility",
          {
            is: "memory_only",
            then: Yup.array()
              .of(
                getMemoryOptionSchema({
                  answerIsRequired,
                  imageRequired
                })
              )
              .test(
                "maxOneActiveAnswer",
                maxOneAnswerMemoryOnly,
                (options) => {
                  if (!Array.isArray(options)) return true;
                  return (
                    options.filter((opt) => !opt._destroy).length <= 1
                  );
                }
              ),
            otherwise: Yup.array()
              .of(
                getOptionSchema({ answerIsRequired, imageRequired })
              )
              .test(
                "atLeastOneCorrect",
                correctAnswerRequired,
                (options) => {
                  if (!Array.isArray(options)) return false;
                  return options
                    .filter((opt) => !opt._destroy)
                    .some((opt) => opt.correct === true);
                }
              )
          }
        ),

        flashcard_explanation_attributes: Yup.object().shape({
          body: Yup.string().required(explanationBodyRequired),
          flashcard_attachments_attributes: Yup.array()
            .of(attachmentSchema)
            .nullable()
        })
      })
    )
  });
};

export const flashCardInitialObj = {
  question_body: "",
  mode_eligibility: "",
  flashcard_attachments_attributes: [],
  difficulty_level: "",
  flashcard_sub_topic_id: null,
  flashcard_topic_id: null,
  flashcard_options_attributes: [
    {
      content: "",
      currentTab: "text",
      flashcard_attachments_attributes: [],
      correct: false
    },
    {
      content: "",
      currentTab: "text",
      flashcard_attachments_attributes: [],
      correct: false
    }
  ],
  flashcard_explanation_attributes: {
    body: "",
    flashcard_attachments_attributes: []
  }
};

export const getInitialValuesAdd = () => ({
  flashcards: [
    { ...flashCardInitialObj, tempId: crypto.randomUUID() }
  ]
});

export const getInitialValuesEdit = (flashcard = {}) => {
  return {
    flashcards: [
      {
        id: flashcard?.id,
        question_code: flashcard?.question_code || "",
        question_body: flashcard?.question_body || "",
        mode_eligibility: flashcard?.mode_eligibility || "",
        // map images array → attachments shape (keep url for preview, path for submit)
        flashcard_attachments_attributes: flashcard?.images?.length
          ? flashcard.images.map((img, i) => ({
              id: img.id,
              path: img.path,
              position: i,
              url: img.url
            }))
          : [],

        difficulty_level: flashcard?.difficulty_level ?? "",
        flashcard_sub_topic_id: flashcard?.sub_topic_id ?? null,
        flashcard_topic_id: flashcard?.topic_id ?? null,

        flashcard_options_attributes: flashcard?.options?.length
          ? flashcard.options.map((option) => ({
              id: option.id,
              content: option.content ?? "",
              correct: option.correct ?? false,
              flashcard_attachments_attributes: option.images?.length
                ? option.images.map((img, i) => ({
                    id: img.id,
                    path: img.path,
                    position: i,
                    url: img.url
                  }))
                : [],
              currentTab:
                option.images?.[0]?.url && option.content
                  ? "text_image"
                  : option.content
                  ? "text"
                  : "image"
            }))
          : flashcard?.mode_eligibility === "memory_only"
          ? [flashCardInitialObj.flashcard_options_attributes[0]]
          : flashCardInitialObj.flashcard_options_attributes,

        flashcard_explanation_attributes: {
          id: flashcard?.explanation?.id,
          body: flashcard?.explanation?.body ?? "",
          flashcard_attachments_attributes: flashcard?.explanation
            ?.images?.length
            ? flashcard.explanation.images.map((img, i) => ({
                path: img.path,
                id: img.id,
                position: i,
                url: img.url
              }))
            : []
        }
      }
    ]
  };
};
